'use client';

import { useState, useEffect, useRef, useCallback } from 'react';
import { MapPin, X, Search } from 'lucide-react';

export interface WhereSelection {
  /** Filtro città (maps to CatalogFiltersState.port). */
  port: string | null;
  zone: string | null;
  port_id: string | null;
  where_label: string | null;
}

interface PoiResult {
  type: 'porto' | 'citta' | 'localita';
  label: string;
  portId?: string;
  city?: string;
  zone?: string;
}

interface Props {
  value: WhereSelection;
  onChange: (v: WhereSelection) => void;
}

const TYPE_BADGE: Record<string, { label: string; cls: string }> = {
  porto:    { label: 'Porto',  cls: 'bg-sky-100 text-sky-700' },
  citta:    { label: 'Città',  cls: 'bg-emerald-100 text-emerald-700' },
  localita: { label: 'Zona',   cls: 'bg-violet-100 text-violet-700' },
};

export default function CatalogWhereInput({ value, onChange }: Props) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<PoiResult[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const hasSelection = !!(value.port || value.zone || value.port_id);
  const displayLabel = value.where_label || value.port || value.zone || null;

  const clear = useCallback(() => {
    onChange({ port: null, zone: null, port_id: null, where_label: null });
    setQuery('');
    setResults([]);
  }, [onChange]);

  useEffect(() => {
    if (query.length < 2) {
      setResults([]);
      setOpen(false);
      return;
    }
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await fetch(`/api/pois/search?q=${encodeURIComponent(query)}`);
        const data = await res.json();
        setResults(data.results || []);
        setOpen(true);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [query]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const select = (r: PoiResult) => {
    if (r.type === 'porto') {
      onChange({
        port: r.city || null,
        zone: r.zone || null,
        port_id: r.portId || null,
        where_label: r.label,
      });
    } else if (r.type === 'citta') {
      onChange({ port: r.city || r.label, zone: null, port_id: null, where_label: r.label });
    } else {
      onChange({ port: null, zone: r.zone || r.label, port_id: null, where_label: r.label });
    }
    setQuery('');
    setOpen(false);
  };

  if (hasSelection && displayLabel) {
    return (
      <div className="flex items-center gap-2" data-cid="catalog-where-selected">
        <div className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-blue-50 border border-blue-200 text-blue-800 font-semibold text-sm">
          <MapPin className="w-4 h-4 text-blue-500 shrink-0" />
          <span>{displayLabel}</span>
          <button
            type="button"
            onClick={clear}
            aria-label="Rimuovi filtro luogo"
            className="ml-1 text-blue-400 hover:text-blue-700 transition-colors"
            data-cid="catalog-where-clear"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div ref={containerRef} className="relative" data-cid="catalog-where-input-wrap">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => results.length > 0 && setOpen(true)}
          placeholder="Dove vuoi andare?"
          className="w-full pl-9 pr-10 py-2.5 rounded-xl border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 placeholder:text-slate-400"
          data-cid="catalog-where-input"
          aria-label="Cerca per porto, città o zona"
          aria-autocomplete="list"
          aria-expanded={open && results.length > 0}
        />
        {loading && (
          <div
            className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"
            aria-hidden
          />
        )}
      </div>

      {open && results.length > 0 && (
        <div
          className="absolute z-50 mt-1.5 w-full min-w-[260px] bg-white rounded-xl border border-slate-200 shadow-xl overflow-hidden"
          role="listbox"
          data-cid="catalog-where-dropdown"
        >
          {results.map((r, i) => {
            const badge = TYPE_BADGE[r.type] ?? { label: r.type, cls: 'bg-slate-100 text-slate-600' };
            return (
              <button
                key={i}
                type="button"
                role="option"
                onClick={() => select(r)}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-slate-50 transition-colors text-sm border-b border-slate-50 last:border-0"
                data-cid={`catalog-where-result-${r.type}`}
              >
                <MapPin className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                <span className="flex-1 text-slate-800 font-medium truncate">{r.label}</span>
                <span className={`shrink-0 text-[10px] font-bold px-1.5 py-0.5 rounded-md ${badge.cls}`}>
                  {badge.label}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
